// The wireframe's `.empty` — one shape for every screen that has nothing to show
// yet (`wireframes/react/src/styles/mud.css:1862`, used from Stocuri.tsx:224,
// Verificare.tsx:158, SolicitariCorectare.tsx:115 and nine more).
//
// A bordered card, a 48px glyph in the secondary border colour, an 18px/26px
// semibold title, and a 14px line that says what WILL appear there and how to
// make it appear. That last line is the point of the whole thing: the wireframe
// answers "why is this blank" everywhere, and the application answered it
// nowhere. It replaces four shapes the application had grown for the same job —
// a full-width blue notification bar, a dashed grey box, a plain grey box with
// one sentence, and a white card with a call to action — which is four different
// answers to a reader who has met the first one already.
//
// AGE ships no empty-state component: `mud-toast` is a message about an
// event, not a surface standing in for absent content, and reading a permanent
// "nothing here yet" as an info alert is the mismatch the reviews kept counting.
// So this is a SIMSM composite over AGE primitives and AGE tokens. No fork, no
// new token.
//
// `EmptyRegisterMessage` is its companion, not a variant: the wireframe never
// replaces a register with a box. On Rapoarte.tsx:107-135 the table card, its
// column headers and its footer all stay, and the message takes one centred cell
// spanning every column, so the reader can still see what the register would
// hold. Same for AdminLocatii.tsx:220 and AdminRegistratori.tsx:494.
import type { ReactNode } from 'react';
import { MudIcon } from '@egov-moldova/mud-react';
import type { IconName } from '@egov-moldova/mud';

export interface EmptyStateProps {
  /** AGE icon name. Drawn at 48px, the wireframe's size. */
  icon: IconName;
  /** What is missing, as a statement or as the next step. One line. */
  title: string;
  /** What will appear here and how to make it appear. Omit only when nothing true can be said. */
  body?: ReactNode;
  /** At most one; the wireframe gives at most one. */
  action?: ReactNode;
  /**
   * Draw the card the wireframe wraps this in on every standalone use. Turn it
   * off where the empty already sits inside a bordered surface, which is what
   * the wireframe itself does on Nefinalizate.tsx:65: two frames would then
   * draw one inside the other.
   */
  framed?: boolean;
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  framed = true,
}: EmptyStateProps): JSX.Element {
  return (
    <div className={framed ? 'simsm-empty simsm-empty--framed' : 'simsm-empty'}>
      {/* MUD's icon sizes stop at 32 (16 / 20 / 24 / 32), so the
          48px glyph is the 24px asset scaled by `--icon-size` from the outer
          tree. This is what the wireframe does too: its icons are a 24 viewBox
          at stroke 1.8, sized by their container (lib/icons.tsx:1). */}
      <span className="simsm-empty__icon" aria-hidden="true">
        <MudIcon name={icon} size={24} />
      </span>
      <p className="simsm-empty__title">{title}</p>
      {body ? <p className="simsm-empty__body">{body}</p> : null}
      {action ? <div className="simsm-empty__action">{action}</div> : null}
    </div>
  );
}

export interface EmptyRegisterMessageProps {
  /** Slotted into `mud-table`, which spans it across every column. */
  children: ReactNode;
}

/**
 * The message for a register that has rows to show but none right now. Pass it
 * as `slot="empty"` inside `<MudTable>` and leave `rows` at `[]`: mud-table keeps
 * the head and draws one `empty-row` whose cell spans the columns.
 */
export function EmptyRegisterMessage({ children }: EmptyRegisterMessageProps): JSX.Element {
  return (
    <span slot="empty" className="simsm-empty-register">
      {children}
    </span>
  );
}
